

import ExcelJS from 'exceljs';
import type { Fill } from 'exceljs';
import { saveAs } from 'file-saver';
import DTOReporteHorarioTE from '../../../DTO/Reportes/DTOReporteHorarioTE';

/*
 * Ahora existen dos columnas fijas:
 *
 * 1 = No. empleado
 * 2 = Nombre
 *
 * Los bloques comienzan desde la columna 3.
 */
const SEMANAS = Array.from({ length: 52 }, (_, index) => index + 1);
const ULTIMA_COLUMNA = 217;

type EmpleadoExcel = {
  numEmpleado: string;
  nombre: string;
  departamento: string;
  registros: Map<number, DTOReporteHorarioTE>;
  ultimo: DTOReporteHorarioTE;
};

type GrupoDepartamento = {
  empleados: EmpleadoExcel[];
};

/*
 * Convierte minutos a HH:MM.
 *
 * Ejemplos:
 * 90 minutos  -> 01:30
 * 1500 minutos -> 25:00
 */
const minutosAHHMM = (minutos: number) =>
  `${Math.floor(minutos / 60)
    .toString()
    .padStart(2, '0')}:${(minutos % 60)
    .toString()
    .padStart(2, '0')}`;

/*
 * Normaliza textos para comparar departamentos:
 *
 * "Ingeniería de Procesos" -> "ingenieria de procesos"
 */
const normalizar = (texto?: string) =>
  (texto || '')
    .toLocaleLowerCase('es-MX')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\./g, '')
    .trim();

/*
 * Colores utilizados actualmente en el Excel de nómina.
 */
const FILLS = {
  VERDE_ING: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFA9D08E' },
  } as Fill,

  ROSITA: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFDE9D9' },
  } as Fill,

  VERDE: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE2EFDA' },
  } as Fill,

  NARANJA: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFFE5CC' },
  } as Fill,

  AZUL: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD6EAF8' },
  } as Fill,

  MORADO: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE8DAEF' },
  } as Fill,

  AMARILLO: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFFF3CD' },
  } as Fill,

  GRIS: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF2F2F2' },
  } as Fill,

  ALERTA: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFF0000' },
  } as Fill,
};

/*
 * Devuelve el color de la fila de acuerdo con el departamento.
 */
const obtenerColorDepartamento = (
  departamento?: string
): Fill | null => {
  const d = normalizar(departamento);

  if (!d || d === 'null') {
    return FILLS.GRIS;
  }

  if (d.includes('oficina general')) {
    return FILLS.GRIS;
  }

  if (d.includes('contabilidad')) {
    return FILLS.ROSITA;
  }

  if (d.includes('ingenieria') && d.includes('procesos')) {
    return FILLS.VERDE_ING;
  }

  
  if (
    d.includes('operaciones') &&
    (d.includes('mty') || d.includes('monterrey'))
  ) {
    return FILLS.NARANJA;
  }

  if (
    d.includes('operaciones') ||
    d.includes('exportacion')
  ) {
    return FILLS.AMARILLO;
  }

  if (d.includes('servicios administrativos')) {
    return FILLS.VERDE;
  }

  if (d.includes('cumplimiento normativo')) {
    return FILLS.AZUL;
  }

  if (d.includes('logistica')) {
    return FILLS.ROSITA;
  }

  return FILLS.GRIS;
};

/*
 * Define los grupos usados para insertar los totales por area.
 *
 * Contabilidad, Oficina General y Servicios Administrativos
 * comparten un mismo subtotal. Los demas departamentos conservan
 * su agrupacion independiente y el orden en que llegan del SP.
 */
const obtenerGrupoDepartamento = (departamento?: string) => {
  const d = normalizar(departamento);

  if (
    d.includes('contabilidad') ||
    d.includes('oficina general') ||
    d.includes('servicios administrativos')
  ) {
    return 'administracion';
  }

  if (d.includes('ingenieria') && d.includes('procesos')) {
    return 'ingenieria-procesos';
  }

  if (
    d.includes('operaciones') &&
    (d.includes('mty') || d.includes('monterrey'))
  ) {
    return 'operaciones-monterrey';
  }

  if (d.includes('operaciones') || d.includes('exportacion')) {
    return 'operaciones-exportacion';
  }

  if (d.includes('cumplimiento normativo')) {
    return 'cumplimiento-normativo';
  }

  if (d.includes('logistica')) {
    return 'logistica';
  }

  return `otro:${d || 'sin-departamento'}`;
};

const agruparEmpleadosPorDepartamento = (
  empleados: Map<string, EmpleadoExcel>
) => {
  const grupos = new Map<string, GrupoDepartamento>();

  empleados.forEach((empleado) => {
    const clave = obtenerGrupoDepartamento(
      empleado.departamento
    );

    if (!grupos.has(clave)) {
      grupos.set(clave, {
        empleados: [],
      });
    }

    grupos.get(clave)!.empleados.push(empleado);
  });

  return Array.from(grupos.values());
};

const bordeDelgado = {
  top: {
    style: 'thin' as const,
    color: { argb: 'FFB7B7B7' },
  },
  left: {
    style: 'thin' as const,
    color: { argb: 'FFB7B7B7' },
  },
  bottom: {
    style: 'thin' as const,
    color: { argb: 'FFB7B7B7' },
  },
  right: {
    style: 'thin' as const,
    color: { argb: 'FFB7B7B7' },
  },
};

const bordeTotal = {
  top: {
    style: 'medium' as const,
    color: { argb: 'FF7F7F7F' },
  },
  left: {
    style: 'medium' as const,
    color: { argb: 'FF7F7F7F' },
  },
  bottom: {
    style: 'medium' as const,
    color: { argb: 'FF7F7F7F' },
  },
  right: {
    style: 'medium' as const,
    color: { argb: 'FF7F7F7F' },
  },
};


const bloques = [
  {
    titulo: 'RETARDOS',
    inicio: 3,
    semanal: (row: DTOReporteHorarioTE) => row.retardos,
    acumulado: (row: DTOReporteHorarioTE) =>
      row.retardosAcumulado,
    esTiempo: false,
  },
  {
    titulo: 'FALTAS',
    inicio: 57,
    semanal: (row: DTOReporteHorarioTE) => row.faltas,
    acumulado: (row: DTOReporteHorarioTE) =>
      row.faltasAcumulado,
    esTiempo: false,
  },
  {
    titulo: 'TIEMPO EXTRA',
    inicio: 111,
    semanal: (row: DTOReporteHorarioTE) =>
      row.minutosTiempoExtra,
    acumulado: (row: DTOReporteHorarioTE) =>
      row.minutosTiempoExtraAcumulado,
    esTiempo: true,
  },
  {
    titulo: 'PERMISOS',
    inicio: 165,
    semanal: (row: DTOReporteHorarioTE) => row.permisos,
    acumulado: (row: DTOReporteHorarioTE) =>
      row.permisosAcumulado,
    esTiempo: false,
  },
];

/*
 * Los ceros se dejan vacíos para evitar saturar visualmente el Excel.
 */
const valorVisible = (
  valor: number,
  esTiempo: boolean
): string | number | null => {
  if (valor <= 0) {
    return null;
  }

  return esTiempo ? minutosAHHMM(valor) : valor;
};

/*
 * Determina en cuál bloque aparecerá el empleado.
 */
const obtenerOficinaReporte = (
  registro: DTOReporteHorarioTE
) => {
  const numeroEmpleado = (registro.numEmpleado || '').trim();

  const oficinaSp = (registro.oficina || '')
    .trim()
    .toLocaleUpperCase('es-MX');

  if (
    numeroEmpleado.startsWith('04') ||
    oficinaSp.includes('MONTERREY') ||
    oficinaSp.includes('MTY')
  ) {
    return 'OFICINA MONTERREY';
  }

  return 'NUEVO LAREDO';
};

export default async function generarExcelReporteHorarioTE(
  registros: DTOReporteHorarioTE[],
  ciclo: number,
  semanaInicio: number,
  semanaFin: number
) {
  /*
   * Estructura:
   *
   * Oficina
   *   └── Empleados
   *         └── Registros por semana
   */
  const oficinas = new Map<
    string,
    Map<string, EmpleadoExcel>
  >();

  registros.forEach((registro) => {
    const oficina = obtenerOficinaReporte(registro);

    if (!oficinas.has(oficina)) {
      oficinas.set(
        oficina,
        new Map<string, EmpleadoExcel>()
      );
    }

    const empleados = oficinas.get(oficina)!;

    const clave =
      (registro.numEmpleado ||
        registro.nombreEmpleado ||
        '').trim();

    if (!empleados.has(clave)) {
      empleados.set(clave, {
        numEmpleado: (registro.numEmpleado || '').trim(),
        nombre: registro.nombreEmpleado || '',
        departamento: registro.departamento || '',
        registros: new Map<number, DTOReporteHorarioTE>(),
        ultimo: registro,
      });
    }

    const empleado = empleados.get(clave)!;

    /*
     * Solo se conserva un registro por empleado y semana.
     */
    empleado.registros.set(registro.semana, registro);

    /*
     * El último registro del empleado contiene los acumulados
     * más recientes dentro del rango consultado.
     */
    if (registro.semana >= empleado.ultimo.semana) {
      empleado.ultimo = registro;

      empleado.numEmpleado =
        registro.numEmpleado || empleado.numEmpleado;

      empleado.nombre =
        registro.nombreEmpleado || empleado.nombre;

      empleado.departamento =
        registro.departamento || empleado.departamento;
    }
  });

  const workbook = new ExcelJS.Workbook();

  workbook.creator = 'GEMCO';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(
    'FALTAS, RETARDOS Y TE',
    {
      properties: {
        defaultRowHeight: 15,
      },
      views: [
        {
          state: 'frozen',

          /*
           * Congela No. empleado y Nombre.
           */
          xSplit: 2,

          /*
           * Congela título, ciclo, bloque y encabezados.
           */
          ySplit: 4,
          showGridLines: false,
        },
      ],
      pageSetup: {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        paperSize: 9,
      },
    }
  );

  /*
   * Título principal.
   */
  worksheet.mergeCells(
    1,
    1,
    1,
    ULTIMA_COLUMNA
  );

  const celdaTitulo = worksheet.getCell(1, 1);

  celdaTitulo.value =
    'Tabla de información de RETARDOS, FALTAS, TIEMPO EXTRA Y PERMISOS';

  celdaTitulo.font = {
    name: 'Arial',
    size: 14,
    bold: true,
  };

  celdaTitulo.alignment = {
    vertical: 'middle',
  };

  /*
   * Ciclo.
   */
  worksheet.mergeCells(
    2,
    1,
    2,
    ULTIMA_COLUMNA
  );

  const celdaCiclo = worksheet.getCell(2, 1);

  celdaCiclo.value = `${ciclo} Actualizado`;

  celdaCiclo.font = {
    name: 'Arial',
    size: 14,
    bold: true,
  };

  celdaCiclo.alignment = {
    horizontal: 'center',
    vertical: 'middle',
  };

  worksheet.getRow(1).height = 21;
  worksheet.getRow(2).height = 21;

  let filaSeccion = 3;

  Array.from(oficinas.entries()).forEach(
    (
      [nombreOficina, empleados],
      indiceOficina
    ) => {
      const filaEncabezados = filaSeccion + 1;

      /*
       * Los empleados empiezan inmediatamente debajo
       * de los encabezados.
       */
      const filaDatos = filaEncabezados + 1;

      /*
       * Los encabezados y totales siguen diferenciándose
       * por oficina.
       */
      const colorEmpleado =
        indiceOficina % 2 === 0
          ? 'FFDDEBF7'
          : 'FFFCE4D6';

      const colorTotal =
        indiceOficina % 2 === 0
          ? 'FF9DC3E6'
          : 'FFF4B183';

      /*
       * Encabezados de los cuatro bloques.
       */
      bloques.forEach((bloque) => {
        const finBloque = bloque.inicio + 52;

        worksheet.mergeCells(
          filaSeccion,
          bloque.inicio,
          filaSeccion,
          finBloque
        );

        const tituloBloque = worksheet.getCell(
          filaSeccion,
          bloque.inicio
        );

        tituloBloque.value = bloque.titulo;
        
        tituloBloque.font = {
          name: 'Calibri',
          size: 8,
          bold: true,
        };

        tituloBloque.alignment = {
          horizontal: 'center',
          vertical: 'middle',
        };

        tituloBloque.border = bordeTotal;

       
        SEMANAS.forEach((semana, index) => {
          const celdaSemana = worksheet.getCell(
            filaEncabezados,
            bloque.inicio + index
          );

          celdaSemana.value = semana;

          celdaSemana.font = {
            name: 'Calibri',
            size: 8,
            bold: true,
          };

          celdaSemana.alignment = {
            horizontal: 'center',
            vertical: 'middle',
          };

          celdaSemana.border = bordeDelgado;
        });

       
        const acumulado = worksheet.getCell(
          filaEncabezados,
          finBloque
        );

        acumulado.value = 'Acumulado';

        acumulado.font = {
          name: 'Calibri',
          size: 8,
          bold: true,
        };

        acumulado.alignment = {
          horizontal: 'center',
          vertical: 'middle',
          wrapText: true,
        };

        acumulado.border = bordeTotal;
      });

      /*
       * Encabezado No. empleado.
       */
      const encabezadoNumero = worksheet.getCell(
        filaEncabezados,
        1
      );

      encabezadoNumero.value = 'No. empleado';

      encabezadoNumero.font = {
        name: 'Arial',
        size: 10,
        bold: true,
      };

      encabezadoNumero.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      };

      encabezadoNumero.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {
          argb: colorEmpleado,
        },
      };

      encabezadoNumero.border = bordeDelgado;

      /*
       * Encabezado Nombre y oficina.
       */
      const encabezadoNombre = worksheet.getCell(
        filaEncabezados,
        2
      );

      encabezadoNombre.value =
        `Nombre - ${nombreOficina.toUpperCase()}`;

      encabezadoNombre.font = {
        name: 'Arial',
        size: 10,
        bold: true,
      };

      encabezadoNombre.alignment = {
        vertical: 'middle',
        wrapText: true,
      };

      encabezadoNombre.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {
          argb: colorEmpleado,
        },
      };

      encabezadoNombre.border = bordeDelgado;

      worksheet.getRow(filaEncabezados).height = 28;

      /*
       * Filas de empleados.
       */
      let filaActual = filaDatos;

      const gruposDepartamento =
        agruparEmpleadosPorDepartamento(empleados);

      gruposDepartamento.forEach((grupo) => {
        grupo.empleados.forEach((empleado) => {
        const fillDepartamento =
          obtenerColorDepartamento(
            empleado.departamento
          ) || FILLS.GRIS;

        /*
         * Aplica el color del departamento a toda la fila,
         * incluyendo las columnas de número, nombre,
         * semanas, acumulados y separadores.
         */
        for (
          let columna = 1;
          columna <= ULTIMA_COLUMNA;
          columna += 1
        ) {
          worksheet.getCell(
            filaActual,
            columna
          ).fill = fillDepartamento;
        }

        /*
         * Número de empleado.
         *
         * Se guarda como texto para conservar ceros
         * a la izquierda, por ejemplo: 010001.
         */
        const numeroEmpleado = worksheet.getCell(
          filaActual,
          1
        );

        numeroEmpleado.value = empleado.numEmpleado;

        numeroEmpleado.numFmt = '@';

        numeroEmpleado.font = {
          name: 'Arial',
          size: 10,
        };
      
        numeroEmpleado.alignment = {
          horizontal: 'center',
          vertical: 'middle',
        };

        numeroEmpleado.border = bordeDelgado;

        /*
         * Nombre del empleado.
         */
        const nombre = worksheet.getCell(
          filaActual,
          2
        );

        nombre.value = empleado.nombre;

        nombre.font = {
          name: 'Arial',
          size: 10,
        };

        nombre.alignment = {
          vertical: 'middle',
        };

        nombre.border = bordeDelgado;

        /*
         * Datos semanales y acumulados.
         */
        bloques.forEach((bloque) => {
          SEMANAS.forEach((semana, index) => {
            const registro =
              empleado.registros.get(semana);

            const valor = registro
              ? bloque.semanal(registro)
              : 0;

            const celda = worksheet.getCell(
              filaActual,
              bloque.inicio + index
            );

            celda.value = valorVisible(
              valor,
              bloque.esTiempo
            );

            if (
              valor >= (bloque.esTiempo ? 180 : 3)
            ) {
              celda.fill = FILLS.ALERTA;
            }

            celda.font = {
              name: 'Calibri',
              size: 8,
            };

            celda.alignment = {
              horizontal: 'center',
              vertical: 'middle',
            };

            celda.border = bordeDelgado;
          });

          const acumulado = worksheet.getCell(
            filaActual,
            bloque.inicio + 52
          );

          acumulado.value = valorVisible(
            bloque.acumulado(empleado.ultimo),
            bloque.esTiempo
          );

          acumulado.font = {
            name: 'Calibri',
            size: 8,
            bold: true,
          };

          acumulado.alignment = {
            horizontal: 'center',
            vertical: 'middle',
          };

          acumulado.border = bordeTotal;
        });

        worksheet.getRow(filaActual).height = 18;

        filaActual += 1;
        });

      /*
       * Total del periodo por area.
       */
      const filaTotal = filaActual;

      worksheet.mergeCells(
        filaTotal,
        1,
        filaTotal,
        2
      );

      const totalTitulo = worksheet.getCell(
        filaTotal,
        1
      );

      totalTitulo.value = 'Total Periodo';

      totalTitulo.font = {
        name: 'Arial',
        size: 10,
        bold: true,
      };

      totalTitulo.alignment = {
        horizontal: 'right',
        vertical: 'middle',
      };

      totalTitulo.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {
          argb: colorTotal,
        },
      };

      totalTitulo.border = bordeTotal;

      bloques.forEach((bloque) => {
        /*
         * Total semanal del bloque.
         */
        SEMANAS.forEach((semana, index) => {
          let total = 0;

          grupo.empleados.forEach((empleado) => {
            const registro =
              empleado.registros.get(semana);

            total += registro
              ? bloque.semanal(registro)
              : 0;
          });

          const celda = worksheet.getCell(
            filaTotal,
            bloque.inicio + index
          );

          celda.value = valorVisible(
            total,
            bloque.esTiempo
          );

          celda.font = {
            name: 'Calibri',
            size: 8,
            bold: true,
          };

          celda.alignment = {
            horizontal: 'center',
            vertical: 'middle',
          };

          celda.border = bordeTotal;
        });

        /*
         * Total acumulado del bloque.
         */
        let totalAcumulado = 0;

        grupo.empleados.forEach((empleado) => {
          totalAcumulado += bloque.acumulado(
            empleado.ultimo
          );
        });

        const acumulado = worksheet.getCell(
          filaTotal,
          bloque.inicio + 52
        );

        acumulado.value = valorVisible(
          totalAcumulado,
          bloque.esTiempo
        );

        acumulado.font = {
          name: 'Calibri',
          size: 8,
          bold: true,
        };

        acumulado.alignment = {
          horizontal: 'center',
          vertical: 'middle',
        };

        acumulado.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {
            argb: colorTotal,
          },
        };

        acumulado.border = bordeTotal;
      });

        worksheet.getRow(filaTotal).height = 18;
        filaActual += 1;
      });

      /*
       * Deja dos filas vacías entre oficinas.
       */
      filaSeccion = filaActual + 2;
    }
  );

  /*
   * Anchos de las columnas fijas.
   */
  worksheet.getColumn(1).width = 16;
  worksheet.getColumn(2).width = 40;

  /*
   * Anchos de semanas, acumulados y separadores.
   */
  bloques.forEach((bloque) => {
    SEMANAS.forEach((_, index) => {
      worksheet.getColumn(
        bloque.inicio + index
      ).width = 4;
    });

    worksheet.getColumn(
      bloque.inicio + 52
    ).width = 12;

    /*
     * La última sección no necesita separador
     * porque termina en la columna 217.
     */
    if (
      bloque.inicio + 53 <=
      ULTIMA_COLUMNA
    ) {
      worksheet.getColumn(
        bloque.inicio + 53
      ).width = 2;
    }
  });

  worksheet.headerFooter.oddFooter =
    `Ciclo ${ciclo} - Semanas ${semanaInicio} a ${semanaFin} &P de &N`;

  const buffer =
    await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  saveAs(
    blob,
    `Reporte Horario y TE Dirección ${ciclo} Semanas ${semanaInicio}-${semanaFin}.xlsx`
  );
}
